/**
 * Project Management Components
 * Components for managing student projects with Kanban board functionality
 */

import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import {
  collection,
  onSnapshot,
  query,
  where,
  orderBy
} from 'firebase/firestore';
import {
  handleAddProject,
  handleUpdateProject,
  handleDeleteProject,
  handleAddSubtask,
  handleUpdateSubtask,
  handleMoveSubtask,
  handleDeleteSubtask,
  handleAddProjectProposal,
  handleUpdateProjectProposal,
  handleApproveProjectProposal,
  handleRejectProjectProposal,
  handleDeleteProjectProposal
} from './dataHandlers';

// Project Management Hook
export const useProjectManagement = ({ currentUser }) => {
  const [projects, setProjects] = useState([]);
  const [myProjects, setMyProjects] = useState([]);
  const [groupProjects, setGroupProjects] = useState([]);
  const [newProject, setNewProject] = useState({
    title: '',
    description: '',
    status: 'in-progress'
  });
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [editProjectForm, setEditProjectForm] = useState({
    title: '',
    description: '',
    status: 'in-progress'
  });
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [projectToComplete, setProjectToComplete] = useState(null);

  // Load projects (including group) from Firebase
  useEffect(() => {
    if (!currentUser) return;

    const qParticipant = query(
      collection(db, 'projects'),
      where('participantIds', 'array-contains', currentUser.uid)
    );

    const unsubscribe = onSnapshot(qParticipant, (snapshot) => {
      const projectsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).filter(p => !p.archived);
      // Sort by createdAt client-side
      projectsData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setProjects(projectsData);
      const isGroupProject = (p) => (p.isGroup || (p.memberIds || []).length > 0);
      const mine = projectsData.filter(p => (p.ownerId || p.userId) === currentUser.uid && !isGroupProject(p));
      const groups = projectsData.filter(p => isGroupProject(p)); // include owner too, participantIds includes owner
      setMyProjects(mine);
      setGroupProjects(groups);
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Add new project
  const handleAddProjectWrapper = async () => {
    if (!newProject.title.trim() || !currentUser) return;

    try {
      await handleAddProject({
        projectData: newProject,
        currentUser
      });

      setNewProject({
        title: '',
        description: '',
        status: 'in-progress'
      });
    } catch (error) {
      alert('Failed to add project. Please try again.');
    }
  };

  // Update project
  const handleUpdateProjectWrapper = async (projectId) => {
    if (!editProjectForm.title.trim()) return;

    try {
      await handleUpdateProject({
        projectId,
        projectData: editProjectForm
      });

      setEditingProjectId(null);
      setEditProjectForm({
        title: '',
        description: '',
        status: 'in-progress'
      });
    } catch (error) {
      alert('Failed to update project. Please try again.');
    }
  };

  // Delete project
  const handleDeleteProjectWrapper = async (projectId) => {
    try {
      await handleDeleteProject({ projectId });
    } catch (error) {
      alert('Failed to delete project. Please try again.');
    }
  };

  // Start editing project
  const handleEditProject = (project) => {
    setEditingProjectId(project.id);
    setEditProjectForm({
      title: project.title,
      description: project.description,
      status: project.status
    });
  };

  // Cancel editing
  const handleCancelEditProject = () => {
    setEditingProjectId(null);
    setEditProjectForm({
      title: '',
      description: '',
      status: 'in-progress'
    });
  };

  // Handle status changes
  const handleStatusChange = async (projectId, newStatus) => {
    if (newStatus === 'completed') {
      const project = projects.find(p => p.id === projectId);
      setProjectToComplete(project);
      setShowCompletionModal(true);
    } else {
      try {
        await handleUpdateProject({
          projectId,
          projectData: { ...projects.find(p => p.id === projectId), status: newStatus }
        });
      } catch (error) {
        alert('Failed to update project status. Please try again.');
      }
    }
  };

  // Handle project deletion
  const handleDeleteProjectFromModal = async () => {
    if (!projectToComplete) return;
    
    try {
      await handleDeleteProject({ projectId: projectToComplete.id });
      setShowCompletionModal(false);
      setProjectToComplete(null);
    } catch (error) {
      alert('Failed to delete project. Please try again.');
    }
  };

  // Cancel completion
  const handleCancelCompletion = () => {
    setShowCompletionModal(false);
    setProjectToComplete(null);
  };

  return {
    projects,
    myProjects,
    groupProjects,
    newProject,
    setNewProject,
    editingProjectId,
    editProjectForm,
    setEditProjectForm,
    showCompletionModal,
    projectToComplete,
    handleAddProject: handleAddProjectWrapper,
    handleUpdateProject: handleUpdateProjectWrapper,
    handleDeleteProject: handleDeleteProjectWrapper,
    handleEditProject,
    handleCancelEditProject,
    handleStatusChange,
    handleDeleteProjectFromModal,
    handleCancelCompletion
  };
};

// Subtask Management Hook
export const useSubtaskManagement = ({ currentUser, projectId }) => {
  const [subtasks, setSubtasks] = useState([]);
  const [newSubtask, setNewSubtask] = useState({
    title: '',
    description: '',
    status: 'todo'
  });
  const [editingSubtaskId, setEditingSubtaskId] = useState(null);
  const [editSubtaskForm, setEditSubtaskForm] = useState({
    title: '',
    description: '',
    status: 'todo'
  });

  // Load subtasks from Firebase
  useEffect(() => {
    if (!currentUser || !projectId) return;

    const q = query(
      collection(db, 'subtasks'),
      where('projectId', '==', projectId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const subtasksData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort by sortOrder, then by createdAt for subtasks without sortOrder
      const sortedSubtasks = subtasksData.sort((a, b) => {
        const aOrder = a.sortOrder !== undefined ? a.sortOrder : 999;
        const bOrder = b.sortOrder !== undefined ? b.sortOrder : 999;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return new Date(a.createdAt) - new Date(b.createdAt);
      });
      setSubtasks(sortedSubtasks);
    });

    return () => unsubscribe();
  }, [currentUser, projectId]);

  // Add new subtask
  const handleAddSubtaskWrapper = async () => {
    if (!newSubtask.title.trim() || !currentUser || !projectId) return;

    try {
      await handleAddSubtask({
        subtaskData: newSubtask,
        projectId,
        currentUser
      });

      setNewSubtask({
        title: '',
        description: '',
        status: 'todo'
      });
    } catch (error) {
      alert('Failed to add subtask. Please try again.');
    }
  };

  // Update subtask
  const handleUpdateSubtaskWrapper = async (subtaskId) => {
    if (!editSubtaskForm.title.trim()) return;

    try {
      await handleUpdateSubtask({
        subtaskId,
        subtaskData: editSubtaskForm
      });

      setEditingSubtaskId(null);
      setEditSubtaskForm({
        title: '',
        description: '',
        status: 'todo'
      });
    } catch (error) {
      alert('Failed to update subtask. Please try again.');
    }
  };

  // Move subtask between columns
  const handleMoveSubtaskWrapper = async (subtaskId, newStatus) => {
    try {
      await handleMoveSubtask({
        subtaskId,
        newStatus,
        projectId
      });
    } catch (error) {
      alert('Failed to move subtask. Please try again.');
    }
  };

  // Delete subtask
  const handleDeleteSubtaskWrapper = async (subtaskId) => {
    try {
      await handleDeleteSubtask({ subtaskId });
    } catch (error) {
      alert('Failed to delete subtask. Please try again.');
    }
  };


  // Start editing subtask
  const handleEditSubtask = (subtask) => {
    setEditingSubtaskId(subtask.id);
    setEditSubtaskForm({
      title: subtask.title,
      description: subtask.description,
      status: subtask.status
    });
  };

  // Cancel editing
  const handleCancelEditSubtask = () => {
    setEditingSubtaskId(null);
    setEditSubtaskForm({
      title: '',
      description: '',
      status: 'todo'
    });
  };

  return {
    subtasks,
    newSubtask,
    setNewSubtask,
    editingSubtaskId,
    editSubtaskForm,
    setEditSubtaskForm,
    handleAddSubtask: handleAddSubtaskWrapper,
    handleUpdateSubtask: handleUpdateSubtaskWrapper,
    handleMoveSubtask: handleMoveSubtaskWrapper,
    handleDeleteSubtask: handleDeleteSubtaskWrapper,
    handleEditSubtask,
    handleCancelEditSubtask
  };
};

// Project Creation Form Component
export const ProjectCreationForm = ({
  newProject,
  setNewProject,
  handleAddProject
}) => {
  return (
    <div className="create-post">
      <h2>Create New Project</h2>
      <p className="transcript-description">For group work, submit a proposal with group members below. Direct create is for individual projects only.</p>
      <div className="form-group">
        <label>Project Title:</label>
        <input
          type="text"
          placeholder="Enter project title"
          value={newProject.title}
          onChange={(e) => setNewProject({ ...newProject, title: e.target.value })}
          className="post-title-input"
        />
      </div>
      <div className="form-group">
        <label>Description:</label>
        <textarea
          placeholder="Describe your project goals and objectives"
          value={newProject.description}
          onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
          rows={4}
        />
      </div>
      <button 
        className="log-activity-button" 
        onClick={handleAddProject}
        disabled={!newProject.title.trim()}
      >
        Create Project
      </button>
    </div>
  );
};

// Project List Component
export const ProjectList = ({
  projects,
  editingProjectId,
  editProjectForm,
  setEditProjectForm,
  handleEditProject,
  handleUpdateProject,
  handleCancelEditProject,
  handleDeleteProject,
  handleStatusChange,
  onSelectProject
}) => {
  return (
    <div className="projects-list">
      <h2>My Projects</h2>
      <p className="transcript-description">
        Manage and track your projects, and submit project proposals for teacher approval.
       </p>
      {projects.length === 0 ? (
        <p className="no-projects">No projects yet. Create your first project to get started!</p>
      ) : (
        <div className="projects-grid">
          {projects.map(project => (
            <div key={project.id} className={`project-card ${project.status === 'on-hold' ? 'project-on-hold' : ''}`}>
              {editingProjectId === project.id ? (
                <div className="project-edit-form">
                  <input
                    type="text"
                    value={editProjectForm.title}
                    onChange={(e) => setEditProjectForm({ ...editProjectForm, title: e.target.value })}
                    className="post-title-input"
                  />
                  <textarea
                    value={editProjectForm.description}
                    onChange={(e) => setEditProjectForm({ ...editProjectForm, description: e.target.value })}
                    rows={3}
                  />
                  <select
                    value={editProjectForm.status}
                    onChange={(e) => setEditProjectForm({ ...editProjectForm, status: e.target.value })}
                  >
                    <option value="in-progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="on-hold">On Hold</option>
                  </select>
                  <div className="project-edit-actions">
                    <button 
                      className="save-button"
                      onClick={() => handleUpdateProject(project.id)}
                    >
                      Save
                    </button>
                    <button 
                      className="cancel-button"
                      onClick={handleCancelEditProject}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="project-content">
                  <div className="project-header">
                    <h3 className="project-title">{project.title}</h3>
                    <span className={`project-status status-${project.status}`}>
                      {project.status.replace('-', ' ')}
                    </span>
                  </div>
                  {project.description && (
                    <p className="project-description">{project.description}</p>
                  )}
                  <div className="project-actions">
                    <button 
                      className="view-button"
                      onClick={() => onSelectProject(project)}
                    >
                      View Kanban Board
                    </button>
                    <button 
                      className="edit-button"
                      onClick={() => handleEditProject(project)}
                    >
                      Edit
                    </button>
                    <button 
                      className="delete-button"
                      onClick={() => handleDeleteProject(project.id)}
                    >
                      Delete
                    </button>
                  </div>
                  {project.status === 'in-progress' && (
                    <div className="project-status-actions">
                      <button 
                        className="cancel-button"
                        onClick={() => handleStatusChange(project.id, 'on-hold')}
                      >
                        Mark as On Hold
                      </button>
                      <button 
                        className="save-button"
                        onClick={() => handleStatusChange(project.id, 'completed')}
                      >
                        Mark as Complete
                      </button>
                    </div>
                  )}
                  {project.status === 'on-hold' && (
                    <div className="project-status-actions">
                      <button 
                        className="edit-button"
                        onClick={() => handleStatusChange(project.id, 'in-progress')}
                      >
                        Resume Project
                      </button>
                      <button 
                        className="save-button"
                        onClick={() => handleStatusChange(project.id, 'completed')}
                      >
                        Mark as Complete
                      </button>
                    </div>
                  )}
                  {project.status === 'completed' && (
                    <div className="project-status-actions">
                      <p className="completion-message">
                        Project completed! Create a reflection to document your work.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Kanban Board Component
export const KanbanBoard = ({
  project,
  subtasks,
  newSubtask,
  setNewSubtask,
  editingSubtaskId,
  editSubtaskForm,
  setEditSubtaskForm,
  handleAddSubtask,
  handleUpdateSubtask,
  handleMoveSubtask,
  handleDeleteSubtask,
  handleEditSubtask,
  handleCancelEditSubtask,
  onBackToProjects
}) => {
  const [draggedSubtask, setDraggedSubtask] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);

  const columns = [
    { id: 'todo', title: 'To Do', color: '#6c757d' },
    { id: 'in-progress', title: 'In Progress', color: '#ffbd39' },
    { id: 'completed', title: 'Completed', color: '#28a745' }
  ];

  const getSubtasksByStatus = (status) => {
    return subtasks.filter(subtask => subtask.status === status);
  };

  // Drag and Drop handlers
  const handleDragStart = (e, subtask) => {
    setDraggedSubtask(subtask);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedSubtask(null);
    setDragOverColumn(null);
  };

  const handleDragOver = (e, columnId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(columnId);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverColumn(null);
    }
  };

  const handleDrop = (e, columnId) => {
    e.preventDefault();
    if (draggedSubtask && draggedSubtask.status !== columnId) {
      handleMoveSubtask(draggedSubtask.id, columnId);
    }
    setDragOverColumn(null);
  };

  return (
    <div className="kanban-board">
      <div className="kanban-header">
        <button 
          className="back-button"
          onClick={onBackToProjects}
        >
          ← Back to Projects
        </button>
        <h2>{project.title}</h2>
        {project.description && (
          <p className="project-description">{project.description}</p>
        )}
      </div>

      <div className="kanban-columns">
        {columns.map(column => (
          <div 
            key={column.id} 
            className={`kanban-column ${dragOverColumn === column.id ? 'drag-over' : ''}`}
            style={dragOverColumn === column.id ? {
              border: `3px solid ${column.color}`,
              backgroundColor: `${column.color}20`,
              borderRadius: '8px',
              boxShadow: `0 0 10px ${column.color}50`
            } : {}}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              handleDragOver(e, column.id);
            }}
            onDragLeave={handleDragLeave}
            onDrop={(e) => {
              e.preventDefault();
              handleDrop(e, column.id);
            }}
          >
            <div className="column-header" style={{ borderTopColor: column.color }}>
              <h3>{column.title}</h3>
              <span className="task-count">
                {getSubtasksByStatus(column.id).length}
              </span>
            </div>
            
            <div className="column-content">
              {getSubtasksByStatus(column.id).map(subtask => (
                <div 
                  key={subtask.id} 
                  className="subtask-card"
                  draggable="true"
                  onDragStart={(e) => handleDragStart(e, subtask)}
                  onDragEnd={handleDragEnd}
                >
                  {editingSubtaskId === subtask.id ? (
                    <div className="subtask-edit-form">
                      <input
                        type="text"
                        value={editSubtaskForm.title}
                        onChange={(e) => setEditSubtaskForm({ ...editSubtaskForm, title: e.target.value })}
                        className="post-title-input"
                      />
                      <textarea
                        value={editSubtaskForm.description}
                        onChange={(e) => setEditSubtaskForm({ ...editSubtaskForm, description: e.target.value })}
                        rows={2}
                      />
                      <select
                        value={editSubtaskForm.status}
                        onChange={(e) => setEditSubtaskForm({ ...editSubtaskForm, status: e.target.value })}
                      >
                        <option value="todo">To Do</option>
                        <option value="in-progress">In Progress</option>
                        <option value="completed">Completed</option>
                      </select>
                      <div className="subtask-edit-actions">
                        <button 
                          className="save-button"
                          onClick={() => handleUpdateSubtask(subtask.id)}
                        >
                          Save
                        </button>
                        <button 
                          className="cancel-button"
                          onClick={handleCancelEditSubtask}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="subtask-content">
                      <h4 className="subtask-title">{subtask.title}</h4>
                      {subtask.description && (
                        <p className="subtask-description">{subtask.description}</p>
                      )}
                      <div className="subtask-actions">
                      </div>
                      <button
                        className="subtask-edit-pencil"
                        onClick={() => handleEditSubtask(subtask)}
                        title="Edit subtask"
                      >
                        <i className="fas fa-pencil-alt"></i>
                      </button>
                      <button
                        className="subtask-delete-x"
                        onClick={() => handleDeleteSubtask(subtask.id)}
                        title="Delete subtask"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                      <div className="drag-hint">
                        <small>Drag to move</small>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Add New Subtask Form */}
      <div className="add-subtask-form">
        <h3>Add New Subtask</h3>
        <div className="form-row">
          <input
            type="text"
            placeholder="Subtask title"
            value={newSubtask.title}
            onChange={(e) => setNewSubtask({ ...newSubtask, title: e.target.value })}
            className="post-title-input"
          />
          <select
            value={newSubtask.status}
            onChange={(e) => setNewSubtask({ ...newSubtask, status: e.target.value })}
          >
            <option value="todo">To Do</option>
            <option value="in-progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>
          <button 
            className="add-button"
            onClick={handleAddSubtask}
            disabled={!newSubtask.title.trim()}
          >
            Add Subtask
          </button>
        </div>
        <textarea
          placeholder="Subtask description (optional)"
          value={newSubtask.description}
          onChange={(e) => setNewSubtask({ ...newSubtask, description: e.target.value })}
          rows={2}
        />
      </div>

      {(project.objectives || project.timeline || project.resources) && (
        <div className="project-extra-info">
          {project.objectives && (
            <div className="project-extra-section">
              <h3>Learning Objectives</h3>
              <p className="project-description">{project.objectives}</p>
            </div>
          )}
          {project.timeline && (
            <div className="project-extra-section">
              <h3>Timeline</h3>
              <p className="project-description">{project.timeline}</p>
            </div>
          )}
          {project.resources && (
            <div className="project-extra-section">
              <h3>Resources</h3>
              <p className="project-description">{project.resources}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Project Completion Modal Component
export const ProjectCompletionModal = ({
  showModal,
  project,
  onDelete,
  onCancel
}) => {
  if (!showModal || !project) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>Delete Project: {project.title}</h2>
        <div className="completion-instructions">
          <p><strong>Before marking this project as complete:</strong></p>
          <ol>
            <li>Copy the project information below</li>
            <li>Go to the "Reflections" page</li>
            <li>Create and submit a new reflection about this project</li>
            <li>Then you can delete this project from your active projects</li>
            <li>Once it's been deleted, you will no longer be able to view it or its subtasks</li>
          </ol>
        </div>
        <div className="project-summary">
          <h3>Project Summary:</h3>
          <p><strong>Title:</strong> {project.title}</p>
          <p><strong>Description:</strong> {project.description}</p>
          <p><strong>Status:</strong> {project.status}</p>
        </div>
        <div className="modal-actions">
          <button 
            className="complete-confirm-button"
            onClick={onDelete}
          >
            Delete Project
          </button>
          <button 
            className="cancel-button"
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Project Management Component
export const ProjectManagementView = ({ currentUser, userProfile }) => {
  const [selectedProject, setSelectedProject] = useState(null);
  
  const {
    myProjects,
    groupProjects,
    editingProjectId,
    editProjectForm,
    setEditProjectForm,
    showCompletionModal,
    projectToComplete,
    handleUpdateProject,
    handleDeleteProject,
    handleEditProject,
    handleCancelEditProject,
    handleStatusChange,
    handleDeleteProjectFromModal,
    handleCancelCompletion
  } = useProjectManagement({ currentUser });

  const {
    projectProposals,
    newProjectProposal,
    setNewProjectProposal,
    allStudents,
    editingProposalId,
    editProposalForm,
    setEditProposalForm,
    handleSubmitProjectProposal,
    handleUpdateProjectProposal,
    handleEditProposal,
    handleCancelEditProposal,
    handleDeleteProjectProposal
  } = useProjectProposalManagement({ currentUser, userProfile });

  // Load all users for name lookup (not just students)
  const [allUsers, setAllUsers] = useState([]);
  useEffect(() => {
    if (!currentUser) return;
    const unsubscribe = onSnapshot(collection(db, 'users'), (snapshot) => {
      const users = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllUsers(users);
    });
    return () => unsubscribe();
  }, [currentUser]);
  
  // Create user lookup map for displaying names
  const usersById = Object.fromEntries((allUsers || []).map(u => [u.id, u]));

  const {
    subtasks,
    newSubtask,
    setNewSubtask,
    editingSubtaskId,
    editSubtaskForm,
    setEditSubtaskForm,
    handleAddSubtask,
    handleUpdateSubtask,
    handleMoveSubtask,
    handleDeleteSubtask,
    handleEditSubtask,
    handleCancelEditSubtask
  } = useSubtaskManagement({ currentUser, projectId: selectedProject?.id });

  if (selectedProject) {
    return (
      <KanbanBoard
        project={selectedProject}
        subtasks={subtasks}
        newSubtask={newSubtask}
        setNewSubtask={setNewSubtask}
        editingSubtaskId={editingSubtaskId}
        editSubtaskForm={editSubtaskForm}
        setEditSubtaskForm={setEditSubtaskForm}
        handleAddSubtask={handleAddSubtask}
        handleUpdateSubtask={handleUpdateSubtask}
        handleMoveSubtask={handleMoveSubtask}
        handleDeleteSubtask={handleDeleteSubtask}
        handleEditSubtask={handleEditSubtask}
        handleCancelEditSubtask={handleCancelEditSubtask}
        onBackToProjects={() => setSelectedProject(null)}
      />
    );
  }

  return (
    <div className="project-management">
      <ProjectList
        projects={myProjects}
        editingProjectId={editingProjectId}
        editProjectForm={editProjectForm}
        setEditProjectForm={setEditProjectForm}
        handleEditProject={handleEditProject}
        handleUpdateProject={handleUpdateProject}
        handleCancelEditProject={handleCancelEditProject}
        handleDeleteProject={handleDeleteProject}
        handleStatusChange={handleStatusChange}
        onSelectProject={setSelectedProject}
      />

      {/* Group Projects Section */}
      {groupProjects.length > 0 && (
        <div className="projects-list">
          <h2>Group Projects</h2>
          <p className="transcript-description">Projects shared with you by classmates.</p>
          <div className="projects-grid">
            {groupProjects.map(project => {
              const isOwner = (project.ownerId || project.userId) === currentUser.uid;
              return (
                <div key={project.id} className={`project-card ${project.status === 'on-hold' ? 'project-on-hold' : ''}`}>
                  {isOwner && editingProjectId === project.id ? (
                    <div className="project-edit-form">
                      <input
                        type="text"
                        value={editProjectForm.title}
                        onChange={(e) => setEditProjectForm({ ...editProjectForm, title: e.target.value })}
                        className="post-title-input"
                      />
                      <textarea
                        value={editProjectForm.description}
                        onChange={(e) => setEditProjectForm({ ...editProjectForm, description: e.target.value })}
                        rows={3}
                      />
                      <select
                        value={editProjectForm.status}
                        onChange={(e) => setEditProjectForm({ ...editProjectForm, status: e.target.value })}
                      >
                        <option value="in-progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="on-hold">On Hold</option>
                      </select>
                      <div className="project-edit-actions">
                        <button 
                          className="save-button"
                          onClick={() => handleUpdateProject(project.id)}
                        >
                          Save
                        </button>
                        <button 
                          className="cancel-button"
                          onClick={handleCancelEditProject}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="project-content">
                      <div className="project-header">
                        <h3 className="project-title">{project.title}</h3>
                        <span className={`project-status status-${project.status}`}>
                          {project.status.replace('-', ' ')}
                        </span>
                      </div>
                      {project.description && (
                        <p className="project-description">{project.description}</p>
                      )}
                      {(project.participantIds || project.memberIds) && (
                        <p className="project-description">
                          <strong>Group Members:</strong> {((project.participantIds || project.memberIds || []).filter(id => id !== currentUser.uid).map(id => {
                            const user = usersById[id];
                            return user?.firstName || user?.name || id;
                          })).join(', ')}
                        </p>
                      )}
                      <div className="project-actions">
                        <button 
                          className="view-button"
                          onClick={() => setSelectedProject(project)}
                        >
                          View Kanban Board
                        </button>
                        {isOwner && (
                          <>
                            <button 
                              className="edit-button"
                              onClick={() => handleEditProject(project)}
                            >
                              Edit
                            </button>
                            <button 
                              className="delete-button"
                              onClick={() => handleDeleteProject(project.id)}
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                      {isOwner && project.status === 'in-progress' && (
                        <div className="project-status-actions">
                          <button 
                            className="cancel-button"
                            onClick={() => handleStatusChange(project.id, 'on-hold')}
                          >
                            Mark as On Hold
                          </button>
                          <button 
                            className="save-button"
                            onClick={() => handleStatusChange(project.id, 'completed')}
                          >
                            Mark as Complete
                          </button>
                        </div>
                      )}
                      {isOwner && project.status === 'on-hold' && (
                        <div className="project-status-actions">
                          <button 
                            className="edit-button"
                            onClick={() => handleStatusChange(project.id, 'in-progress')}
                          >
                            Resume Project
                          </button>
                          <button 
                            className="save-button"
                            onClick={() => handleStatusChange(project.id, 'completed')}
                          >
                            Mark as Complete
                          </button>
                        </div>
                      )}
                      {project.status === 'completed' && (
                        <div className="project-status-actions">
                          <p className="completion-message">
                            Project completed! Create a reflection to document your work.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      <div className="legacy-workflows-section"></div>
      
      <ProjectProposalForm
        newProjectProposal={newProjectProposal}
        setNewProjectProposal={setNewProjectProposal}
        handleSubmitProjectProposal={handleSubmitProjectProposal}
        allStudents={allStudents}
        currentUser={currentUser}
      />
      
      <ProjectProposalsDisplay
        projectProposals={projectProposals}
        currentUser={currentUser}
        editingProposalId={editingProposalId}
        editProposalForm={editProposalForm}
        setEditProposalForm={setEditProposalForm}
        handleUpdateProjectProposal={handleUpdateProjectProposal}
        handleEditProposal={handleEditProposal}
        handleCancelEditProposal={handleCancelEditProposal}
        handleDeleteProjectProposal={handleDeleteProjectProposal}
        usersById={usersById}
      />
      
      <ProjectCompletionModal
        showModal={showCompletionModal}
        project={projectToComplete}
        onDelete={handleDeleteProjectFromModal}
        onCancel={handleCancelCompletion}
      />
    </div>
  );
};

// Teacher Read-Only Project List Component
export const TeacherProjectList = ({ 
  projects, 
  onSelectProject,
  studentName 
}) => {  
  return (
    <div className="projects-list">
      {projects.length === 0 ? (
        <p className="no-projects">No active projects.</p>
      ) : (
        <div className="projects-grid">
          {projects.map(project => (
            <div key={project.id} className={`project-card ${project.status === 'on-hold' ? 'project-on-hold' : ''}`}>
              <div className="project-content">
                <div className="project-header">
                  <h3 className="project-title">{project.title}</h3>
                  <span className={`project-status status-${project.status}`}>
                    {project.status.replace('-', ' ')}
                  </span>
                </div>
                {project.description && (
                  <p className="project-description">{project.description}</p>
                )}
                <div className="project-actions">
                  <button 
                    className="view-button"
                    onClick={() => onSelectProject(project)}
                  >
                    View Kanban Board
                  </button>
                </div>
                {project.status === 'completed' && (
                  <div className="project-status-actions">
                    <p className="completion-message">
                      Project completed!
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Teacher Read-Only Kanban Board Component
export const TeacherKanbanBoard = ({ 
  project, 
  subtasks, 
  onBack 
}) => {
  const columns = [
    { id: 'todo', title: 'To Do', color: '#6c757d' },
    { id: 'in-progress', title: 'In Progress', color: '#ffbd39' },
    { id: 'completed', title: 'Completed', color: '#28a745' }
  ];

  const getSubtasksByStatus = (status) => {
    return subtasks
      .filter(subtask => subtask.status === status)
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  };

  return (
    <div className="kanban-board">
      <div className="kanban-header">
        <h2>{project.title}</h2>
        <div className="project-info">
          <span className={`project-status status-${project.status}`}>
            {project.status.replace('-', ' ')}
          </span>
          <button 
            className="kanban-close-x"
            onClick={onBack}
            title="Close kanban board"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
      </div>
      
      {project.description && (
        <div className="project-description-section">
          <p>{project.description}</p>
        </div>
      )}

      <div className="kanban-columns">
        {columns.map(column => (
          <div key={column.id} className="kanban-column">
            <div className="column-header" style={{ borderTopColor: column.color }}>
              <h3>{column.title}</h3>
              <span className="task-count">
                {getSubtasksByStatus(column.id).length}
              </span>
            </div>
            <div className="column-content">
              {getSubtasksByStatus(column.id).map(subtask => (
                <div key={subtask.id} className="subtask-card teacher-view">
                  <div className="subtask-content">
                    <h4 className="subtask-title">{subtask.title}</h4>
                    {subtask.description && (
                      <p className="subtask-description">{subtask.description}</p>
                    )}
                  </div>
                </div>
              ))}
              {getSubtasksByStatus(column.id).length === 0 && (
                <div className="empty-column">
                  <p>No tasks</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Teacher Project Management View Component
export const TeacherProjectManagementView = ({ 
  currentUser, 
  selectedStudentUid,
  studentName = 'Student',
  userProfile
}) => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [subtasks, setSubtasks] = useState([]);

  const {
    projectProposals,
    handleApproveProjectProposal,
    handleRejectProjectProposal,
    handleDeleteProjectProposal,
    allStudents
  } = useProjectProposalManagement({ currentUser, userProfile });

  // Filter project proposals by selected student (for transcript view)
  const studentProjectProposals = selectedStudentUid && selectedStudentUid !== 'all' 
    ? projectProposals.filter(proposal => proposal.studentId === selectedStudentUid)
    : projectProposals;

  // Filter for active proposals (pending and rejected, not approved)
  const activeStudentProposals = studentProjectProposals.filter(proposal => proposal.status !== 'approved');

  // Listen to projects for the selected student (both owned and group via participantIds)
  useEffect(() => {
    if (!selectedStudentUid) {
      return;
    }

    const ownedQuery = query(
      collection(db, 'projects'),
      where('userId', '==', selectedStudentUid)
    );
    const participantQuery = query(
      collection(db, 'projects'),
      where('participantIds', 'array-contains', selectedStudentUid)
    );

    const unsubscribeOwned = onSnapshot(ownedQuery, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(p => !p.archived);
      setProjects(prev => {
        // merge with any existing participant results (they might update in parallel)
        const existing = new Map((prev || []).map(p => [p.id, p]));
        data.forEach(p => existing.set(p.id, p));
        return Array.from(existing.values()).filter(p => !p.archived);
      });
    });

    const unsubscribeParticipant = onSnapshot(participantQuery, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(p => !p.archived);
      setProjects(prev => {
        const existing = new Map((prev || []).map(p => [p.id, p]));
        data.forEach(p => existing.set(p.id, p));
        return Array.from(existing.values()).filter(p => !p.archived);
      });
    });

    return () => {
      unsubscribeOwned();
      unsubscribeParticipant();
    };
  }, [selectedStudentUid]);

  // Listen to subtasks for the selected project
  useEffect(() => {
    if (!selectedProject?.id) {
      setSubtasks([]);
      return;
    }

    const subtasksQuery = query(
      collection(db, 'subtasks'),
      where('projectId', '==', selectedProject.id)
    );

    const unsubscribe = onSnapshot(subtasksQuery, (snapshot) => {
      const subtasksData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSubtasks(subtasksData);
    });

    return () => unsubscribe();
  }, [selectedProject]);

  const handleSelectProject = (project) => {
    setSelectedProject(project);
  };

  const handleBackToProjects = () => {
    setSelectedProject(null);
  };

  if (selectedProject) {
    return (
      <TeacherKanbanBoard
        project={selectedProject}
        subtasks={subtasks}
        onBack={handleBackToProjects}
      />
    );
  }

  return (
    <div>
      {/* Only show Project Proposals section if there are active proposals */}
      {activeStudentProposals.length > 0 && (
        <ProjectProposalManagementView
          projectProposals={studentProjectProposals}
          handleApproveProjectProposal={handleApproveProjectProposal}
          handleRejectProjectProposal={handleRejectProjectProposal}
          handleDeleteProjectProposal={handleDeleteProjectProposal}
          usersById={Object.fromEntries((allStudents || []).map(s => [s.id, s]))}
        />
      )}
      
      <TeacherProjectList
        projects={projects}
        onSelectProject={handleSelectProject}
        studentName={studentName}
      />
    </div>
  );
};

// Project Proposal Management Hook
export const useProjectProposalManagement = ({ currentUser, userProfile }) => {
  const [projectProposals, setProjectProposals] = useState([]);
  const [newProjectProposal, setNewProjectProposal] = useState({
    title: '',
    description: '',
    objectives: '',
    timeline: '',
    resources: '',
    memberIds: []
  });
  const [allStudents, setAllStudents] = useState([]);
  const [editingProposalId, setEditingProposalId] = useState(null);
  const [editProposalForm, setEditProposalForm] = useState({
    title: '',
    description: '',
    objectives: '',
    timeline: '',
    resources: ''
  });

  // Listen for project proposals
  useEffect(() => {
    if (!currentUser) return;

    const qProjectProposals = query(collection(db, 'projectProposals'), orderBy('timestamp', 'desc'));
    const unsubscribeProjectProposals = onSnapshot(qProjectProposals, (snapshot) => {
      const proposalsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProjectProposals(proposalsData);
    });

    // Load students for group selection
    const unsubscribeStudents = onSnapshot(collection(db, 'users'), (snapshot) => {
      const users = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Only students
      const studentsOnly = users.filter(u => (u.role || 'student') === 'student');
      setAllStudents(studentsOnly);
    });

    return () => {
      unsubscribeProjectProposals();
      unsubscribeStudents();
    };
  }, [currentUser]);

  // Submit a new project proposal (student)
  const handleSubmitProjectProposal = async () => {
    if (!newProjectProposal.title.trim() || !newProjectProposal.description.trim() || !newProjectProposal.objectives.trim()) {
      return;
    }

    try {
      await handleAddProjectProposal({
        proposalData: newProjectProposal,
        currentUser,
        userProfile
      });
      
      setNewProjectProposal({
        title: '',
        description: '',
        objectives: '',
        timeline: '',
        resources: '',
        memberIds: []
      });
      
    } catch (error) {
      alert('Failed to submit proposal. Please try again.');
    }
  };

  // Approve a project proposal (teacher)
  const handleApproveProjectProposalWrapper = async (proposalId) => {
    try {
      await handleApproveProjectProposal({
        proposalId,
        currentUser,
        userProfile
      });
    } catch (error) {
      alert('Failed to approve proposal. Please try again.');
    }
  };

  // Reject a project proposal (teacher)
  const handleRejectProjectProposalWrapper = async (proposalId) => {
    try {
      await handleRejectProjectProposal({
        proposalId,
        currentUser,
        userProfile
      });
    } catch (error) {
      alert('Failed to reject proposal. Please try again.');
    }
  };

  // Update a project proposal
  const handleUpdateProjectProposalWrapper = async (proposalId) => {
    if (!editProposalForm.title.trim() || !editProposalForm.description.trim()) {
      return;
    }

    try {
      await handleUpdateProjectProposal({
        proposalId,
        proposalData: editProposalForm
      });

      setEditingProposalId(null);
      setEditProposalForm({
        title: '',
        description: '',
        objectives: '',
        timeline: '',
        resources: ''
      });
    } catch (error) {
      alert('Failed to update proposal. Please try again.');
    }
  };

  // Delete a project proposal
  const handleDeleteProjectProposalWrapper = async (proposalId) => {
    try {
      await handleDeleteProjectProposal({ proposalId });
    } catch (error) {
      alert('Failed to delete proposal. Please try again.');
    }
  };

  // Start editing a proposal
  const handleEditProposal = (proposal) => {
    setEditingProposalId(proposal.id);
    setEditProposalForm({
      title: proposal.title || '',
      description: proposal.description || '',
      objectives: proposal.objectives || '',
      timeline: proposal.timeline || '',
      resources: proposal.resources || ''
    });
  };

  // Cancel editing
  const handleCancelEditProposal = () => {
    setEditingProposalId(null);
    setEditProposalForm({
      title: '',
      description: '',
      objectives: '',
      timeline: '',
      resources: ''
    });
  };

  return {
    projectProposals,
    newProjectProposal,
    setNewProjectProposal,
    allStudents,
    editingProposalId,
    editProposalForm,
    setEditProposalForm,
    handleSubmitProjectProposal,
    handleUpdateProjectProposal: handleUpdateProjectProposalWrapper,
    handleEditProposal,
    handleCancelEditProposal,
    handleApproveProjectProposal: handleApproveProjectProposalWrapper,
    handleRejectProjectProposal: handleRejectProjectProposalWrapper,
    handleDeleteProjectProposal: handleDeleteProjectProposalWrapper
  };
};

// Project Proposal Form Component (Student)
const GroupMemberSelector = ({ students, currentUserId, selectedMemberIds, onChange }) => {
  const selectableStudents = (students || []).filter(s => s.id !== currentUserId);
  const toggleMember = (id) => {
    if (selectedMemberIds.includes(id)) {
      onChange(selectedMemberIds.filter(mid => mid !== id));
    } else {
      onChange([...selectedMemberIds, id]);
    }
  };
  return (
    <div className="form-group group-member-selector">
      <label>Group Members (Optional)</label>
      <div className="group-member-grid">
        {selectableStudents.map(s => (
          <button
            type="button"
            key={s.id}
            className={selectedMemberIds.includes(s.id) ? 'tag-filter active' : 'tag-filter'}
            onClick={() => toggleMember(s.id)}
            title={s.email || s.name}
          >
            {s.firstName || s.name || s.email || s.id}
          </button>
        ))}
      </div>
      {selectedMemberIds.length > 0 && (
        <p className="transcript-description">Selected: {selectedMemberIds.length}</p>
      )}
    </div>
  );
};

export const ProjectProposalForm = ({ newProjectProposal, setNewProjectProposal, handleSubmitProjectProposal, allStudents, currentUser }) => {
  return (
    <div className="create-post">
      <h2>Submit Project Proposal</h2>
      <p className="transcript-description">
        Submit a project proposal for teacher approval. Once approved, you'll be able to create your project and start working on your Kanban board.
      </p>
      
      <div className="form-group">
        <label>Project Title: <span style={{color: 'var(--color-error)'}}>*</span></label>
        <input
          type="text"
          placeholder="e.g., Developing a Mobile App for a Local Business"
          value={newProjectProposal.title}
          onChange={(e) => setNewProjectProposal({ ...newProjectProposal, title: e.target.value })}
          className="post-title-input"
        />
      </div>

      <div className="form-group">
        <label>Project Description: <span style={{color: 'var(--color-error)'}}>*</span></label>
        <textarea
          placeholder="Describe your project in detail. What will you be building, creating, researching, or doing?"
          value={newProjectProposal.description}
          onChange={(e) => setNewProjectProposal({ ...newProjectProposal, description: e.target.value })}
          className="post-description-input"
          rows="4"
        />
      </div>

      <div className="form-group">
        <label>Learning Objectives: <span style={{color: 'var(--color-error)'}}>*</span></label>
        <textarea
          placeholder="What specific skills or knowledge do you hope to gain from this project?"
          value={newProjectProposal.objectives}
          onChange={(e) => setNewProjectProposal({ ...newProjectProposal, objectives: e.target.value })}
          className="post-description-input"
          rows="3"
        />
      </div>

      <div className="form-group">
        <label>Timeline (Optional)</label>
        <textarea
          placeholder="Estimated timeline for completing this project (e.g., 4 weeks, 2 months, end of semester 4, etc.)"
          value={newProjectProposal.timeline}
          onChange={(e) => setNewProjectProposal({ ...newProjectProposal, timeline: e.target.value })}
          className="post-description-input"
          rows="2"
        />
      </div>

      <div className="form-group">
        <label>Resources Needed (Optional)</label>
        <textarea
          placeholder="What resources, tools, or materials will you need for this project?"
          value={newProjectProposal.resources}
          onChange={(e) => setNewProjectProposal({ ...newProjectProposal, resources: e.target.value })}
          className="post-description-input"
          rows="3"
        />
      </div>

      <GroupMemberSelector
        students={allStudents}
        currentUserId={currentUser?.uid}
        selectedMemberIds={newProjectProposal.memberIds}
        onChange={(memberIds) => setNewProjectProposal({ ...newProjectProposal, memberIds })}
      />

      <button 
        onClick={handleSubmitProjectProposal} 
        className="class-assignment-button"
        disabled={!newProjectProposal.title.trim() || !newProjectProposal.description.trim() || !newProjectProposal.objectives.trim()}
      >
        Submit Proposal
      </button>
    </div>
  );
};

// Student's Own Project Proposals Display Component
export const ProjectProposalsDisplay = ({ 
  projectProposals, 
  currentUser,
  editingProposalId,
  editProposalForm,
  setEditProposalForm,
  handleUpdateProjectProposal,
  handleEditProposal,
  handleCancelEditProposal,
  handleDeleteProjectProposal,
  usersById
}) => {
  // Show proposals created by the student or where the student is a selected group member, excluding approved
  const myProposals = projectProposals.filter(proposal => {
    const isCreator = proposal.studentId === currentUser.uid;
    const isGroupMember = (proposal.memberIds || []).includes(currentUser.uid);
    return (isCreator || isGroupMember) && proposal.status !== 'approved';
  });
  
  if (myProposals.length === 0) {
    return null;
  }

  return (
    <div className="post">
      <h3>Pending Project Proposals</h3>
      <div className="mentor-requests-list">
        {myProposals.map((proposal) => {
          const isEditing = editingProposalId === proposal.id;
          const canEdit = proposal.status === 'pending' || proposal.status === 'rejected';
          
          return (
            <div key={proposal.id} className="mentor-request-item">
              {isEditing ? (
                <div className="proposal-edit-form">
                  <div className="form-group">
                    <label>Project Title: <span style={{color: 'var(--color-error)'}}>*</span></label>
                    <input
                      type="text"
                      placeholder="e.g., Mobile App Development Project"
                      value={editProposalForm.title}
                      onChange={(e) => setEditProposalForm({ ...editProposalForm, title: e.target.value })}
                      className="post-title-input"
                    />
                  </div>

                  <div className="form-group">
                    <label>Project Description: <span style={{color: 'var(--color-error)'}}>*</span></label>
                    <textarea
                      placeholder="Describe your project in detail. What will you be building or creating?"
                      value={editProposalForm.description}
                      onChange={(e) => setEditProposalForm({ ...editProposalForm, description: e.target.value })}
                      className="post-description-input"
                      rows="4"
                    />
                  </div>

                  <div className="form-group">
                    <label>Learning Objectives (Optional)</label>
                    <textarea
                      placeholder="What specific skills or knowledge do you hope to gain from this project?"
                      value={editProposalForm.objectives}
                      onChange={(e) => setEditProposalForm({ ...editProposalForm, objectives: e.target.value })}
                      className="post-description-input"
                      rows="3"
                    />
                  </div>

                  <div className="form-group">
                    <label>Timeline (Optional)</label>
                    <textarea
                      placeholder="Estimated timeline for completing this project (e.g., 4 weeks, 2 months, etc.)"
                      value={editProposalForm.timeline}
                      onChange={(e) => setEditProposalForm({ ...editProposalForm, timeline: e.target.value })}
                      className="post-description-input"
                      rows="2"
                    />
                  </div>

                  <div className="form-group">
                    <label>Resources Needed (Optional)</label>
                    <textarea
                      placeholder="What resources, tools, or materials will you need for this project?"
                      value={editProposalForm.resources}
                      onChange={(e) => setEditProposalForm({ ...editProposalForm, resources: e.target.value })}
                      className="post-description-input"
                      rows="3"
                    />
                  </div>

                  <div className="proposal-edit-actions">
                    <button 
                      className="save-button"
                      onClick={() => handleUpdateProjectProposal(proposal.id)}
                      disabled={!editProposalForm.title.trim() || !editProposalForm.description.trim()}
                    >
                      Save
                    </button>
                    <button 
                      className="cancel-button"
                      onClick={handleCancelEditProposal}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="mentor-request-header">
                <strong className="mentor-request-status">
                  {proposal.title}
                  {(proposal.memberIds || []).length > 0 && (
                    <span className="mentor-request-pending-badge" style={{ marginLeft: '8px' }}>
                      Group Project
                    </span>
                  )}
                  {proposal.status === 'approved' && (
                    <span className="mentor-request-completed-badge">
                      <i className="fa-solid fa-check"></i> Approved
                    </span>
                  )}
                  {proposal.status === 'rejected' && (
                    <span className="lor-request-overdue-badge">
                      <i className="fa-solid fa-times"></i> Rejected
                    </span>
                  )}
                  {proposal.status === 'pending' && (
                    <span className="mentor-request-pending-badge">
                      <i className="fa-solid fa-clock"></i> Pending Review
                    </span>
                  )}
                </strong>
                <span className="timestamp">
                  Submitted: {new Date(proposal.timestamp).toLocaleDateString()}
                </span>
              </div>
              <div className="mentor-request-description">
                <strong>Description:</strong> {proposal.description}
              </div>
              {(proposal.memberIds || []).length > 0 && (
                <div className="mentor-request-description">
                  <strong>Group Members:</strong> { (proposal.memberIds || []).map(id => (usersById?.[id]?.firstName || usersById?.[id]?.name || id)).join(', ') }
                </div>
              )}
              {proposal.objectives && (
                <div className="mentor-request-description">
                  <strong>Learning Objectives:</strong> {proposal.objectives}
                </div>
              )}
              {proposal.timeline && (
                <div className="mentor-request-description">
                  <strong>Timeline:</strong> {proposal.timeline}
                </div>
              )}
              {proposal.resources && (
                <div className="mentor-request-description">
                  <strong>Resources:</strong> {proposal.resources}
                </div>
              )}
              {proposal.status === 'approved' && proposal.approvedAt && (
                <div className="lor-request-complete-info">
                  <i className="fa-solid fa-check lor-request-check-icon"></i>
                  Approved on {new Date(proposal.approvedAt).toLocaleDateString()}
                  {proposal.approvedByName && ` by ${proposal.approvedByName}`}
                </div>
              )}
              {proposal.status === 'rejected' && proposal.approvedAt && (
                <div className="lor-request-complete-info" style={{color: 'var(--color-error)'}}>
                  <i className="fa-solid fa-times lor-request-check-icon"></i>
                  Rejected on {new Date(proposal.approvedAt).toLocaleDateString()}
                  {proposal.approvedByName && ` by ${proposal.approvedByName}`}
                </div>
              )}
              <div className="proposal-actions" style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
                {canEdit && (
                  <button 
                    className="edit-button"
                    onClick={() => handleEditProposal(proposal)}
                  >
                    Edit
                  </button>
                )}
                <button 
                  className="delete-button"
                  onClick={() => handleDeleteProjectProposal(proposal.id)}
                >
                  Delete
                </button>
              </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Teacher Project Proposal Management View Component
export const ProjectProposalManagementView = ({ 
  projectProposals, 
  handleApproveProjectProposal, 
  handleRejectProjectProposal, 
  handleDeleteProjectProposal,
  usersById
}) => {
  // Filter out approved proposals since they appear in Current Projects
  const activeProposals = projectProposals.filter(proposal => proposal.status !== 'approved');
  
  return (
    <div className="post post-margin-bottom">
      <h2>Project Proposals</h2>
      <p className="transcript-description">
        Review and manage project proposals from students. Approve proposals to allow students to create their projects.
      </p>

      {activeProposals.length === 0 ? (
        <p className="no-posts">No active project proposals.</p>
      ) : (
        <>
          {/* Pending Proposals */}
          {(() => {
            const pendingProposals = activeProposals.filter(proposal => proposal.status === 'pending');
            if (pendingProposals.length === 0) return null;
            
            return (
              <>
                <h3 style={{ color: 'var(--color-primary)', marginTop: '20px', marginBottom: '15px' }}>Pending Proposals</h3>
                {pendingProposals.map((proposal) => {
                  return (
                    <div key={proposal.id} id={`project-proposal-${proposal.id}`} className="lor-request-item">
                      <div className="lor-request-header">
                        <div>
                          <h4 className="lor-request-student-name">{proposal.studentName}</h4>
                          <p className="lor-request-email">{proposal.studentEmail}</p>
                        </div>
                        <div className="lor-request-deadline">
                          <strong>Submitted:</strong> {new Date(proposal.timestamp).toLocaleDateString()}
                        </div>
                      </div>

                      <div className="lor-request-body">
                        <div className="lor-request-field">
                          <strong>Project Title:</strong>
                          <p>
                            {proposal.title}
                            {(proposal.memberIds || []).length > 0 && (
                              <span className="mentor-request-pending-badge" style={{ marginLeft: '8px' }}>Group Project</span>
                            )}
                          </p>
                        </div>

                        <div className="lor-request-field">
                          <strong>Description:</strong>
                          <p>{proposal.description}</p>
                        </div>

                        {(proposal.memberIds || []).length > 0 && (
                          <div className="lor-request-field">
                            <strong>Group Members:</strong>
                            <p>{ (proposal.memberIds || []).map(id => (usersById?.[id]?.firstName || usersById?.[id]?.name || id)).join(', ') }</p>
                          </div>
                        )}

                        {proposal.objectives && (
                          <div className="lor-request-field">
                            <strong>Learning Objectives:</strong>
                            <p>{proposal.objectives}</p>
                          </div>
                        )}

                        {proposal.timeline && (
                          <div className="lor-request-field">
                            <strong>Timeline:</strong>
                            <p>{proposal.timeline}</p>
                          </div>
                        )}

                        {proposal.resources && (
                          <div className="lor-request-field">
                            <strong>Resources:</strong>
                            <p>{proposal.resources}</p>
                          </div>
                        )}
                      </div>

                      <div className="lor-request-actions">
                        <button 
                          onClick={() => handleApproveProjectProposal(proposal.id)} 
                          className="save-btn"
                        >
                          Approve Proposal
                        </button>
                        <button 
                          onClick={() => handleRejectProjectProposal(proposal.id)} 
                          className="delete-btn"
                        >
                          Reject Proposal
                        </button>
                      </div>
                    </div>
                  );
                })}
              </>
            );
          })()}

          {/* Rejected Proposals */}
          {(() => {
            const rejectedProposals = activeProposals.filter(proposal => proposal.status === 'rejected');
            if (rejectedProposals.length === 0) return null;
            
            return (
              <>
                <h3 style={{ color: 'var(--color-error)', marginTop: '40px', marginBottom: '15px' }}>Rejected Proposals</h3>
                {rejectedProposals.map((proposal) => (
                  <div key={proposal.id} id={`project-proposal-${proposal.id}`} className="lor-request-item lor-request-rejected">
                    <div className="lor-request-header">
                      <div>
                        <h4 className="lor-request-student-name">{proposal.studentName}</h4>
                        <p className="lor-request-email">{proposal.studentEmail}</p>
                      </div>
                      <div className="lor-request-overdue-badge">
                        <i className="fa-solid fa-times"></i> Rejected
                      </div>
                    </div>

                    <div className="lor-request-body">
                      <div className="lor-request-field">
                        <strong>Project Title:</strong>
                        <p>{proposal.title}</p>
                      </div>

                      <div className="lor-request-timestamp">
                        Rejected on {new Date(proposal.approvedAt).toLocaleDateString()}
                        {proposal.approvedByName && ` by ${proposal.approvedByName}`}
                      </div>
                    </div>

                    <div className="lor-request-actions">
                      <button 
                        onClick={() => handleDeleteProjectProposal(proposal.id)} 
                        className="delete-btn"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </>
            );
          })()}
        </>
      )}
    </div>
  );
};
